import { useState, useEffect } from 'react'
import { UserCartAddonItemType, USER_CART_ADDON_TYPES } from 'constants/userCart'
import { CartItemData } from 'hooks/UserCart/useAllUserCartItemsMetadata'
import { isExperienceItem, isFlightItem, isTransferItem } from 'lib/checkout/checkoutUtils'
import { isUserCartAccommodationItem } from 'lib/checkout/userCart'
import { FlightViewTypes } from 'constants/flight'

export type BannerTypeVariant = 'pastDates' | 'soldOut' | 'unavailable' | undefined

export function getBannerTypeVariant(
  isPast?: boolean,
  isSoldOut?: boolean,
  isUnavailable?: boolean,
  isError?: boolean,
): BannerTypeVariant {
  if (isPast) return 'pastDates'
  if (isSoldOut) return 'soldOut'
  if (isUnavailable || isError) return 'unavailable'
  return undefined
}

interface HotelSavingsResult {
  totalSavings: number
}

export function calculateHotelLuxPlusDetails(cartItems: Array<CartItemData>): HotelSavingsResult {
  const hotelItems = cartItems.filter(item => (item.itemType === 'hotel' || item.itemType === 'bedbankHotel') &&
    !item.isDeleted &&
    !item.isSavedForLater
  )

  const totalSavings = hotelItems
    .filter(item => item.luxPlus?.hasMemberPrices)
    .reduce((total, item) => {
      const regularPrice = item.itemTotals?.price ?? 0
      const memberPrice = item.itemTotals?.memberPrice ?? 0
      return total + (regularPrice - memberPrice)
    }, 0)

  return {
    totalSavings,
  }
}

export function filterOutAddonItems(cartItemsData: Array<CartItemData>): Array<CartItemData> {
  return cartItemsData.filter(
    cartItem => !USER_CART_ADDON_TYPES.includes(cartItem.itemType as UserCartAddonItemType),
  )
}

function formatTimeRemaining(remaining: number) {
  if (remaining <= 0) {
    return '00:00'
  }

  const minutes = Math.floor(remaining / 1000 / 60)
  const seconds = Math.floor((remaining / 1000) % 60)
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

export function useFlightExpiryTimer(expiry: number): { timeRemaining: string; isExpired: boolean } {
  const [timeRemaining, setTimeRemaining] = useState<number>(() => expiry - Date.now())

  useEffect(() => {
    if (!expiry) return

    const timer = setInterval(() => {
      const remaining = expiry - Date.now()

      if (remaining <= 0) {
        setTimeRemaining(0)
        clearInterval(timer)
      } else {
        setTimeRemaining(remaining)
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [expiry])

  return { timeRemaining: formatTimeRemaining(timeRemaining), isExpired: timeRemaining <= 0 }
}

export function getCartItemDate(item: App.Checkout.AnyItem): Date {
  if (isUserCartAccommodationItem(item) && 'checkIn' in item && item.checkIn) {
    return new Date(item.checkIn)
  }

  if (isFlightItem(item)) {
    return item.flights?.[0]?.departingDate ? new Date(item.flights[0].departingDate) : new Date()
  }

  if (isExperienceItem(item)) {
    return item.date ? new Date(item.date) : new Date()
  }

  if (isTransferItem(item)) {
    return item.transfer?.date ? new Date(item.transfer.date) : new Date()
  }

  return new Date()
}

export function isTwoOneWaysItem(flightItem: App.Checkout.FlightItem) {
  return flightItem.viewType === FlightViewTypes.TWO_ONE_WAYS &&
    flightItem.flights.length > 1 &&
    flightItem.flights[0].provider !== flightItem.flights[1].provider
}
